import * as React from 'react'

import { MobXProviderContext, observer } from 'mobx-react'

import { PermitFieldType } from '~/client/graph'
import DrillDownFilter from '~/client/src/shared/components/DrillDownFilter'
import type { IDrillDownFilterCategory } from '~/client/src/shared/components/DrillDownFilter/DrillDownFilter.types'
import {
  decodeDrillDownFilterSelectionId,
  encodeDrillDownFilterSelectionId,
} from '~/client/src/shared/components/DrillDownFilter/filterSelectionIds'
import { ListHeader } from '~/client/src/shared/components/ListHeader'
import { ListSelector } from '~/client/src/shared/components/ListSelector'
import {
  IListSelectorGroupConfig,
  IListSelectorGroupHeaderInfo,
} from '~/client/src/shared/components/ListSelector/types'
import { PERMIT_FIELD_GLYPHS } from '~/client/src/shared/components/PermitFieldIcon/permitFieldGlyphs'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import InactiveWorkflowTypeInfo from '~/client/src/shared/components/WorkflowTypeListSelector/InactiveWorkflowTypeInfo'
import { ManageWorkflowTypesContext } from '~/client/src/shared/components/WorkflowTypeListSelector/ManageWorkflowTypesContext'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { compareGroupLabelsUnassignedLast } from '~/client/src/shared/utils/compareGroupsByLabel'
import { toast } from '~/client/src/shared/utils/toast'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import { diffAllowances, IAllowanceEdit } from './resourceFieldAllowanceWrites'
import { useResourceFieldAllowances } from './useResourceFieldAllowances'
import { useSaveResourceFieldAllowances } from './useSaveResourceFieldAllowances'

// One flat row per field. Everything about the workflow type travels on the row so the type
// can be expressed as a group key rather than an item, which is what gives its band
// tri-state, a subtree toggle and an (n) badge for free - a leaf row can only ever render a
// plain boolean.
interface IFieldRow {
  id: string
  typeId: string
  fieldId: string
  fieldType: PermitFieldType
  typeName: string
  categoryName: string
  iconName: string
  isInactive: boolean
  ordinal: string
  label: string
}

// Filter facets. Stable and unlocalized: they are encoded into the selection ids, while the
// labels come from the translator at render.
const STATUS_FACET = 'status'
const CATEGORY_FACET = 'category'
const ACTIVE_STATUS = 'active'
const INACTIVE_STATUS = 'inactive'

// Joins the fields of a row into its content signature. A category name is user-entered free
// text, so the separator has to be a character it can never contain: NUL, not a printable one.
const SEPARATOR = '\u0000'

interface IFilterOption {
  id: string
  label: string
}

const optionOf = (
  facet: string,
  value: string,
  label: string,
): IFilterOption => ({
  id: encodeDrillDownFilterSelectionId(facet, value),
  label,
})

interface IProps {
  resourceTypeId: string
  /**
   * The record whose allowances seed the ticks. Omit for a bulk edit over a selection: the
   * list then opens with nothing ticked, because a leaf row has no tri-state to show a
   * field that allows some of the selected records and not others.
   */
  recordId?: string
  /**
   * Replaces the default single-record save. Receives an explicit answer for EVERY row the
   * list showed, built against those rows - not row ids for the caller to re-resolve, which
   * would break the moment a workflow type is versioned while this is open.
   */
  onApply?: (edits: IAllowanceEdit[]) => void
  /**
   * Turns the record's WR toggle on, for a record that is off. Called before the default save
   * when anything is left ticked, and awaited: enabling denies the record on every restricted
   * field, so the save is diffed against that state and lands on top of it. Ignored when
   * onApply is set.
   */
  onEnable?: () => Promise<void>
  /**
   * Open with every row ticked rather than with the record's stored allowances - for a
   * resource that cannot be booked at all, whose allowances the cascade has stripped, so
   * the useful starting point is "allowed everywhere" rather than an empty list.
   */
  selectAllByDefault?: boolean
  title?: string
  onClose?: () => void
  /** Sizes the Root. It must resolve a height - the list scrolls inside it. */
  className?: string
  /** Blocks Apply. The checkboxes still move, the draft being local; nothing is committed. */
  isApplyDisabled?: boolean
}

/**
 * The Workflow Types control's selector, listing each workflow type's fields OF THIS RESOURCE
 * TYPE rather than the types alone.
 *
 * A row is checked when this resource is allowed by that field's own allow-list - the same
 * array "Allow to select" edits in the workflow configurator, so the two surfaces show one
 * state rather than two that can disagree.
 *
 * Banded by workflow type, the one level that owns the fields. Status (Active / Not Active)
 * and category are filters rather than groupings.
 */
function WorkflowTypeFieldSelector({
  resourceTypeId,
  recordId,
  onApply,
  onEnable,
  selectAllByDefault,
  title,
  onClose,
  className,
  isApplyDisabled,
}: IProps) {
  const groups = useResourceFieldAllowances(resourceTypeId, recordId ?? '')
  const { isMobileEnv } = React.useContext(MobXProviderContext) as {
    isMobileEnv?: boolean
  }
  const manageInConfigurator = React.useContext(ManageWorkflowTypesContext)
  const saveAllowances = useSaveResourceFieldAllowances(recordId ?? '')

  // `groups` is rebuilt on every render - the hook projects observables straight through -
  // so the identity that matters has to come from the CONTENT. Draft mode re-seeds its
  // checkboxes whenever `selectedIds` changes identity, and a fresh Set each render would
  // wipe every toggle the moment it was made.
  const built = React.useMemo(() => {
    const rows: IFieldRow[] = []
    const selected = new Set<string>()
    const selectedOnceEnabled = new Set<string>()

    groups.forEach(group => {
      group.fields.forEach(field => {
        rows.push({
          id: field.id,
          typeId: group.typeId,
          fieldId: field.fieldId,
          fieldType: field.fieldType,
          typeName: group.typeName,
          categoryName: group.categoryName,
          iconName: group.iconName,
          isInactive: group.isInactive,
          ordinal: field.ordinal,
          label: field.label,
        })
        // The record's TRUE allowances. Bulk has no single record to read one from, so
        // it stays empty there.
        if (recordId && field.isAllowed) selected.add(field.id)
        if (recordId && field.isAllowedOnceEnabled) {
          selectedOnceEnabled.add(field.id)
        }
      })
    })

    return {
      items: rows,
      selectedIds: selected,
      selectedIdsOnceEnabled: selectedOnceEnabled,
      signature: rows
        .map(row =>
          [
            row.id,
            row.fieldType,
            row.ordinal,
            row.label,
            row.typeName,
            row.categoryName,
            row.iconName,
            row.isInactive,
            selected.has(row.id),
            selectedOnceEnabled.has(row.id),
          ].join(SEPARATOR),
        )
        .join('\n'),
    }
  }, [groups, recordId])

  const { items, selectedIds, selectedIdsOnceEnabled } = React.useMemo(
    () => built,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable by content, not reference
    [built.signature],
  )

  // What the list OPENS with, which is not always what is stored. selectAllByDefault ticks
  // everything; `selectedIds` stays the record's real allowances so the Apply diff is taken
  // against the truth. Conflating the two made "accept the default" a no-op diff - nothing
  // saved, nothing enabled - and any edit down a pure list of denials.
  const draftSeed = React.useMemo(
    () =>
      selectAllByDefault ? new Set(items.map(item => item.id)) : selectedIds,
    [selectAllByDefault, items, selectedIds],
  )

  // Any one row describes its type's band - they all carry the same type data. Doubles as
  // the test for whether a key's last segment is a type id at all.
  const typeById = React.useMemo(() => {
    const byId = new Map<string, IFieldRow>()
    items.forEach(item => {
      if (!byId.has(item.typeId)) byId.set(item.typeId, item)
    })
    return byId
  }, [items])

  // The workflow type is the only band: it owns the fields, and without it the rows are a run
  // of fields all called "Location" with nothing saying which workflow they belong to. Status
  // and category narrow the list as filters instead, so there is nothing to regroup by.
  const grouping = React.useMemo<IListSelectorGroupConfig<IFieldRow>>(() => {
    const labelOf = (typeId: string): string =>
      typeById.get(typeId)?.typeName ?? typeId

    return {
      options: [],
      defaultKey: null,
      defaultCollapsed: true,
      ungroupedConfig: {
        groupBy: item => [item.typeId],
        getGroupLabel: labelOf,
        sortGroups: (a, b) => labelOf(a).localeCompare(labelOf(b)),
      },
      buildGroupConfig: () => undefined,
    }
  }, [typeById])

  const [filterSelection, setFilterSelection] = React.useState<string[]>([])

  const filterCategories = React.useMemo<
    IDrillDownFilterCategory<IFilterOption>[]
  >(() => {
    const categoryNames = Array.from(
      new Set(items.map(item => item.categoryName)),
    ).sort(compareGroupLabelsUnassignedLast)

    return [
      {
        id: STATUS_FACET,
        label: Localization.translator.activeNotActive,
        items: [
          optionOf(STATUS_FACET, ACTIVE_STATUS, Localization.translator.active),
          optionOf(
            STATUS_FACET,
            INACTIVE_STATUS,
            Localization.translator.notActive,
          ),
        ],
        getItemId: option => option.id,
        getItemLabel: option => option.label,
      },
      {
        id: CATEGORY_FACET,
        label: Localization.translator.category,
        items: categoryNames.map(name =>
          optionOf(
            CATEGORY_FACET,
            name,
            name === UNASSIGNED ? Localization.translator.unassigned : name,
          ),
        ),
        getItemId: option => option.id,
        getItemLabel: option => option.label,
      },
    ]
  }, [items])

  // Values OR within a facet, facets AND. Only what is SHOWN narrows: the draft keeps every
  // row's tick, and Apply still answers for all of `items`, hidden rows included.
  const visibleItems = React.useMemo(() => {
    const statuses = new Set<string>()
    const categories = new Set<string>()
    filterSelection.forEach(id => {
      const decoded = decodeDrillDownFilterSelectionId(id)
      if (decoded?.facetKey === STATUS_FACET) statuses.add(decoded.optionId)
      if (decoded?.facetKey === CATEGORY_FACET) categories.add(decoded.optionId)
    })

    if (!statuses.size && !categories.size) return items

    return items.filter(
      item =>
        (!statuses.size ||
          statuses.has(item.isInactive ? INACTIVE_STATUS : ACTIVE_STATUS)) &&
        (!categories.size || categories.has(item.categoryName)),
    )
  }, [items, filterSelection])

  const search = React.useMemo(
    () => ({
      searchFn: (item: IFieldRow, query: string) =>
        item.label.toLowerCase().includes(query) ||
        item.ordinal.includes(query) ||
        item.typeName.toLowerCase().includes(query) ||
        item.categoryName.toLowerCase().includes(query),
      inputTestId: 'workflow-type-fields-search-input',
    }),
    [],
  )

  const renderItem = React.useCallback(
    (item: IFieldRow, isSelected: boolean, onToggle: () => void) => {
      const Glyph = PERMIT_FIELD_GLYPHS[item.fieldType]

      return (
        <ListSelector.Row
          isSelected={isSelected}
          onToggle={onToggle}
          testId={`workflow-type-field-row-${item.id}`}
          contentClassName="tw:flex tw:flex-1 tw:items-center tw:gap-2 tw:min-w-0"
        >
          <Glyph
            className="tw:size-4 tw:shrink-0 tw:text-muted-foreground"
            aria-hidden="true"
          />
          <span className="tw:text-label-lg tw:shrink-0 tw:text-muted-foreground tw:tabular-nums">
            {item.ordinal}
          </span>
          {/* Always foreground. A field is a field; it is the TYPE that can be deactivated,
            and its band says so - greying the rows read as the fields being unavailable. */}
          <span className="tw:text-label-lg tw:truncate tw:text-foreground">
            {item.label}
          </span>
        </ListSelector.Row>
      )
    },
    [],
  )

  // Only a type band resolves here; a category or Active/Not Active band spans several
  // types, so it has neither an icon nor an inactive state of its own.
  const typeOfGroup = React.useCallback(
    (group: IListSelectorGroupHeaderInfo<IFieldRow>) => typeById.get(group.key),
    [typeById],
  )

  const renderGroupContent = React.useCallback(
    (group: IListSelectorGroupHeaderInfo<IFieldRow>) => {
      const type = typeOfGroup(group)
      if (!type) return null
      // No iconName still draws: WorkflowTypeIcon falls back to the generic workflow mark.
      return (
        <WorkflowTypeIcon
          iconName={type.iconName}
          size="small"
          className={type.isInactive ? 'tw:opacity-40' : ''}
        />
      )
    },
    [typeOfGroup],
  )

  // An inactive workflow type mutes its own LABEL: the fields underneath stay black, so the
  // band is the only place that can say the type cannot be created right now.
  const getGroupHeaderProps = React.useCallback(
    (group: IListSelectorGroupHeaderInfo<IFieldRow>) => {
      if (!typeOfGroup(group)?.isInactive) return undefined
      return {
        labelClassName: 'tw:text-muted-foreground',
        trailing: (
          <InactiveWorkflowTypeInfo
            isMobile={!!isMobileEnv}
            onManage={manageInConfigurator ?? undefined}
          />
        ),
      }
    },
    [typeOfGroup, isMobileEnv, manageInConfigurator],
  )

  return (
    <ListSelector.Root
      items={visibleItems}
      selectedIds={draftSeed}
      grouping={grouping}
      search={search}
      onClose={onClose}
      draft
      className={className ?? 'tw:h-full tw:w-full tw:bg-background'}
    >
      {title && <ListSelector.Title title={title} onClose={onClose} />}
      <ListSelector.Toolbar
        filter={
          <ListHeader.Filter badgeCount={filterSelection.length}>
            <DrillDownFilter.Platform
              title={Localization.translator.filterBy}
              categories={filterCategories}
              initialSelection={filterSelection}
              onApply={setFilterSelection}
            />
          </ListHeader.Filter>
        }
      />
      <ListSelector.Counter>
        {total => (
          <span className="tw:text-label-lg tw:text-muted-foreground">
            {total} {Localization.translator.fields}
          </span>
        )}
      </ListSelector.Counter>
      <ListSelector.List
        renderItem={renderItem}
        renderGroupContent={renderGroupContent}
        getGroupHeaderProps={getGroupHeaderProps}
      />
      {/* Apply is shown and disabled rather than hidden: the rows already display the real
          stored state, and leaving the button in place says the editing half is coming here
          rather than somewhere else. */}
      {/* One Apply, however many workflow types the edits span: the diff is grouped by type
          and committed as a single save. Closing afterwards is deliberate - the rows would
          otherwise re-seed underneath the user as the saved types land back in the store. */}
      <ListSelector.Actions
        onApply={draftIds => {
          if (onApply) {
            // Every listed row gets an answer, so the caller can replace outright; the
            // typeId travels with it rather than being looked up again later.
            //
            // Closing is the CALLER's, not ours: a bulk apply is async, and the popover
            // has to stay up until it settles. Closing here instead would free the same
            // selection for a second bulk action while the first is still in flight, and
            // whichever request resolved last would decide the result.
            onApply(
              items.map(item => ({
                typeId: item.typeId,
                fieldId: item.fieldId,
                shouldAllow: draftIds.has(item.id),
              })),
            )
            return
          }

          // Fire-and-forget by design here - this path closes immediately - but the
          // promise still has to be handled, or a failed save is an unhandled rejection
          // instead of something the user is told about. Applying with nothing ticked is
          // a revoke, not an assignment, and enables nothing.
          const isEnabling = !!onEnable && draftIds.size > 0
          const edits = diffAllowances(
            items,
            isEnabling ? selectedIdsOnceEnabled : selectedIds,
            draftIds,
          )
          ;(isEnabling ? onEnable() : Promise.resolve())
            .then(() =>
              saveAllowances(
                edits,
                isEnabling
                  ? { resourceTypeId, recordIds: [recordId ?? ''] }
                  : undefined,
              ),
            )
            .catch(() =>
              toast.error(Localization.translator.failedToSaveChangesTryAgain),
            )
          onClose?.()
        }}
        onClose={onClose}
        isApplyDisabled={isApplyDisabled}
      />
    </ListSelector.Root>
  )
}

export default observer(WorkflowTypeFieldSelector)
